import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import htmlParser from "html-react-parser";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay,Keyboard, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import "./index.css";

const ReviewsSection = ({ data, cdnUrl }) => {
  const [reviews, setReviews] = useState([]);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const config = data?.reviewsSection || {};

  useEffect(() => { 
    fetchReviews(pageNumber);
  }, []);

  const fetchReviews = async (page) => {
    setLoading(true);
    
    try {
      const response = await axios.post(
        process.env.REACT_APP_NEW_REVIEWS_API,
        { pageNumber: page }
      );
      
      if (response.status === 200) {
        const newTestimonials = response.data;
     
        if (Array.isArray(newTestimonials)) {
          const transformedReviews = newTestimonials.map((ele) => {
            const date = new Date(ele.survey_completed_on);
            date.setDate(date.getDate() - 1);
            const monthNames = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];
            const formattedDate = `on ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            const lastName = `${ele.customer_last_name.charAt(0)}.`;
            const firstName = ele.customer_first_name;
            
            return {
              id: ele.id,
              text: ele.review,
              rating: ele.rating,
              date: formattedDate,
              author: `${lastName}, ${formattedDate}`,
              name: `${firstName} ${lastName}`,
              isExpanded: false
            };
          });
          
          setReviews((prev) => [...prev, ...transformedReviews]);
          
          if (transformedReviews.length >= 8) {
            setPageNumber((prev) => prev + 1);
          }
        }
      } else {
        throw new Error('Request failed');
      }
      
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const toggleReviewExpansion = (reviewId) => {
    setReviews((prevReviews) =>
      prevReviews.map((review) =>
        review.id === reviewId
          ? { ...review, isExpanded: !review.isExpanded }
          : review
      )
    );
  };

  const renderStars = (rating) => {
    return (
      <div className="review-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? "star-filled" : "star-empty"}>
            ★
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="reviews-section">
        <Container>
          <div className="reviews-loading">Loading reviews...</div>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <></>
    );
  }

  if (reviews.length === 0) {
    return (
      <section className="reviews-section">
        <Container>
          <div className="reviews-empty">
            <p>No reviews available at this time.</p>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="reviews-section">
      <Container className="reviews-container">
        <Row className="reviews-header">
          <Col lg={12} className="text-center">
            <h2 
              className="reviews-heading"
              style={{ color: config.sectionTitle?.color || "#FF5601" }}
            >
              {config.sectionTitle?.text ? htmlParser(config.sectionTitle.text) : "Our Clients Say It Best"}
            </h2>
            <p className="reviews-subheading">
              {config.sectionDescription ? htmlParser(config.sectionDescription) : "From smooth closings to life-changing results, our clients' words say it all. Here's what homeowners across the country have to say about their Sun West experience."}
            </p>
          </Col>
        </Row>

        <div className="reviews-carousel-wrapper">
        <Swiper
  modules={[Navigation, Autoplay, Keyboard, Mousewheel]}
  keyboard={{
    enabled: true,
    onlyInViewport: true  
  }}
  mousewheel={{
    enabled: true,
    forceToAxis: true,        
    releaseOnEdges: true,     
    sensitivity: 1           
  }}
  spaceBetween={30}
  slidesPerView={1}
  autoplay={{
    delay: 3000,
    disableOnInteraction: false,
    pauseOnMouseEnter: true
  }}
  speed={800}
  loop={reviews.length >= 6}  // Changed this
  
  // ADD THESE:
  allowTouchMove={true}
  slideToClickedSlide={false}
  watchSlidesProgress={true}
  
  breakpoints={{
    768: { slidesPerView: 2 },
    992: { slidesPerView: 3 }
  }}
  onSwiper={setSwiperInstance}
  className="reviews-swiper-container"
>
            {reviews.map((review) => (
              <SwiperSlide key={review.id} className="reviews-swiper-slide">
                <div className="review-card-content">
                  <div className="d-flex review-stars-div">
                    {renderStars(review.rating)}
                    <p className="review-date">{review.date}</p>
                  </div>
                  <div className={`review-text ${review.isExpanded ? 'expanded' : 'collapsed'}`}>
                    {htmlParser(review.text)}
                  </div>
                  {review.text.length > 200 && (
                    <button
                      className="see-more-btn"
                      onClick={() => toggleReviewExpansion(review.id)}
                    >
                      {review.isExpanded ? 'See less >' : 'See more >'}
                    </button>
                  )}
                  <div className="review-author-info">
                    <p className="review-author-name">{review.name}</p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="reviews-navigation-arrows">
          <button 
            className="reviews-arrow-btn reviews-arrow-left" 
            onClick={() => swiperInstance?.slidePrev()}
            aria-label="Previous slide"
          >
            <img 
              src={`${cdnUrl}left-arrow.webp`} 
              alt="Previous" 
              className="arrow-image"
            />
          </button>

          <button 
            className="reviews-arrow-btn reviews-arrow-right" 
            onClick={() => swiperInstance?.slideNext()}
            aria-label="Next slide"
          >
            <img 
              src={`${cdnUrl}right-arrow.webp`} 
              alt="Next" 
              className="arrow-image"
            />
          </button>
        </div>
      </Container>
    </section>
  );
};

export default ReviewsSection;